import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Lets any screen tell Remi what record is currently on screen.
 *
 * ChatMS calls page context the single highest-value item on the integration list:
 * a biller looking at a claim types "why was this denied?" and it just works, instead
 * of being asked to retype the claim number.
 *
 * Route-derived context (see usePageContext) covers screens whose id is in the URL.
 * This provider covers the ones where it isn't - Claim Status V2, Denial Management,
 * Workable Claims, Posting Payments V2 - where the id lives in a selected table row.
 */

const RemiStore = createContext(null);

export function RemiContextProvider({ children }) {
  const [published, setPublished] = useState({});

  const publish = useCallback((partial) => setPublished(partial || {}), []);
  const clear = useCallback(() => setPublished({}), []);

  const value = useMemo(() => ({ published, publish, clear }), [published, publish, clear]);
  return <RemiStore.Provider value={value}>{children}</RemiStore.Provider>;
}

export function useRemiStore() {
  return useContext(RemiStore) || { published: {}, publish: () => {}, clear: () => {} };
}

/**
 * Publish what's on screen. Call from a screen component:
 *
 *   usePublishPageContext(selectedRow && {
 *     ClaimID:   selectedRow.ClaimID,
 *     PatientID: selectedRow.PatientID,
 *     sMRN:      selectedRow.MRN,
 *     sDOS:      selectedRow.DOS,
 *   });
 *
 * Every field is optional - send what the screen has. Passing null/undefined clears it.
 * Automatically cleared on unmount so context never outlives the screen that set it.
 */
export function usePublishPageContext(partial) {
  const { publish, clear } = useRemiStore();
  // Serialise so a new object literal each render doesn't retrigger the effect.
  const key = JSON.stringify(partial || {});

  useEffect(() => {
    publish(partial || {});
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, publish, clear]);
}
